/**
 * Converts degrees to radians.
 * @param degrees Number of rotational degrees.
 * @returns Radial length calculated from provided degrees.
 */
export function getRadiansFromDegrees(degrees: number): number {
	return (degrees * Math.PI) / 180;
}

type Dimensions = {
	height: number;
	width: number;
};

/**
 * Transforms box dimensions via a provided number of rotational degrees. Calculates new bounding box dimensions
 * required to contain the original rotated box.
 * @param originalDimensions Dimensions of the original box.
 * @param rotation Number of degrees to rotate the box.
 * @returns Box dimensions for space required to contain original rotated box.
 */
export function transformDimensionWithRotation(originalDimensions: Dimensions, rotation: number): Dimensions {
	const { width, height } = originalDimensions;
	const rotationInRadians = getRadiansFromDegrees(rotation);

	return {
		height: Math.abs(Math.sin(rotationInRadians) * width) + Math.abs(Math.cos(rotationInRadians) * height),
		width: Math.abs(Math.cos(rotationInRadians) * width) + Math.abs(Math.sin(rotationInRadians) * height)
	};
}

/**
 * Computes the maximum value of `values`. If `values` is empty, `null`, or `undefined`, then `undefined` is returned.
 * @param values The collection of values to iterate over.
 * @returns Returns the maximum value.
 */
export function getMax(values: Array<number> | null | undefined): number | undefined {
	if (!values?.length) {
		return undefined;
	}

	let currentMax = 0;

	for (const value of values) {
		if (value > currentMax) {
			currentMax = value;
		}
	}

	return currentMax;
}

/**
 * Clamps a value between a minimum and maximum bound.
 * @param value The number to clamp.
 * @param min The lower bound.
 * @param max The upper bound.
 * @returns The clamped value.
 */
export function clamp(value: number, min: number, max: number): number {
	return Math.min(Math.max(value, min), max);
}
